import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Search, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCMS } from '../context/CMSContext';
import { blogs as defaultBlogs } from '../data/mockData';

const PostCard = ({ post, index, featured }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ delay: index * 0.05, duration: 0.55 }}
    className={`group relative overflow-hidden rounded-2xl border border-white/5 hover:border-white/20 transition-colors duration-500 ${featured ? 'md:col-span-2 lg:col-span-3' : ''}`}
    data-testid={`blog-card-${post.slug}`}
  >
    <Link to={`/blog/${post.slug}`} className="block">
      <div className={`relative ${featured ? 'aspect-[16/8]' : 'aspect-[16/10]'} overflow-hidden`}>
        <img src={post.image} alt={post.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
          loading="lazy" />
      </div>
      <div className="p-6 lg:p-8">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold mb-4">
          <span className="px-3 py-1 rounded-full border border-white/15">{post.category}</span>
          <span>{post.date}</span>
          <span className="w-1 h-1 rounded-full bg-white/30" />
          <span>{post.readTime}</span>
        </div>
        <h3 className={`font-display font-medium tracking-tight text-white leading-snug ${featured ? 'text-2xl lg:text-4xl' : 'text-xl lg:text-2xl'}`}>
          {post.title}
        </h3>
        <p className="mt-3 text-white/50 text-sm font-light leading-relaxed line-clamp-2">
          {post.excerpt}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-white text-sm font-semibold">
          <span className="border-b border-white/30 group-hover:border-white pb-0.5 transition-colors">Read article</span>
          <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
        </div>
      </div>
    </Link>
  </motion.article>
);

const BlogPage = () => {
  const { get } = useCMS();
  const posts = get('blogs') || defaultBlogs;
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return ['All', ...Array.from(set)];
  }, [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (category !== 'All' && p.category !== category) return false;
      if (!q) return true;
      return (
        (p.title || '').toLowerCase().includes(q) ||
        (p.excerpt || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
      );
    });
  }, [posts, category, query]);

  const featured = filtered.find((p) => p.featured) || filtered[0];
  const rest = filtered.filter((p) => p.id !== featured?.id);

  return (
    <div className="min-h-screen bg-[#050914]">
      <Navbar />

      <section className="relative pt-36 pb-14 lg:pt-44 lg:pb-16 overflow-hidden" data-testid="blog-page-header">
        <div className="absolute inset-0 hero-mesh" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
            <span className="w-8 h-px bg-white/30" /> Insights
          </p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter text-white leading-[1.02] max-w-4xl">
            Sharp thinking on digital, design and growth.
          </h1>
          <p className="mt-7 text-white/60 text-lg max-w-2xl font-light leading-relaxed">
            Occasional, opinionated writing on building premium digital experiences for industrial and automotive businesses.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="relative pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex flex-wrap gap-2 items-end">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setCategory(cat)}
                aria-pressed={category === cat}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-bold transition-all duration-300 ${
                  category === cat
                    ? 'bg-white text-[#050914] -translate-y-2 shadow-lg shadow-black/40'
                    : 'border border-white/10 text-white/60 hover:text-white hover:border-white/30 translate-y-0'
                }`}
                data-testid={`blog-filter-${cat.toLowerCase().replace(/\W+/g, '-')}`}>
                {cat}
              </button>
            ))}
          </div>
          <div className="md:ml-auto relative w-full md:w-72">
            <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-11 pr-9 py-2.5 rounded-full bg-white/5 border border-white/10 text-white text-sm placeholder-white/40 focus:border-white/30 focus:outline-none transition-colors"
              data-testid="blog-search-input"
              aria-label="Search articles"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors" aria-label="Clear search">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="relative pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {filtered.length === 0 ? (
            <p className="text-center text-white/50 py-16">No articles match your filters.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured && <PostCard post={featured} index={0} featured />}
              {rest.map((p, i) => <PostCard key={p.id} post={p} index={i + 1} />)}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
